"use client";

import React from "react";
import { reportClientError } from "@/lib/errors/client";

// Class-based error boundary at the root layout. Catches errors thrown
// during render of any descendant. Async errors / event handler errors
// are NOT caught by error boundaries — those go through window.error
// (auto-captured via initClientErrorCapture).
//
// Renders a friendly fallback that doesn't leak stack details to the
// user. Includes a "reload" button so they're not stranded on a blank
// page.

type Props = { children: React.ReactNode };

type State = { hasError: boolean };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    reportClientError({
      error,
      componentStack: info.componentStack ?? undefined,
      context: { boundary: "root" },
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-xl font-semibold">Something went wrong.</h1>
          <p className="text-sm text-muted-foreground">
            The page hit an unexpected error and our team has been notified. You can try
            reloading — if it keeps happening, contact support.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
